import { create } from 'zustand';
import type { PersonaId } from '@/types/persona';
import {
  personas,
  DEFAULT_PERSONA_ID,
  type Persona,
} from '@/data/personas';

/**
 * Persona Store — SPEC-FRONTEND_V4 §12.
 * Tracks currently active persona (V4) while retaining legacy activePersona
 * filter field (V3) for backward compatibility with existing panels.
 */

interface UserLike {
  personaId?: string;
  id?: string;
  email?: string;
}

interface PersonaState {
  // V3 legacy
  activePersona: PersonaId | 'all';

  // V4 fields
  currentPersonaId: string;

  // Actions
  setPersona: (persona: PersonaId | 'all') => void;
  togglePersona: (persona: PersonaId) => void;
  setCurrentPersona: (personaId: string) => void;
  getCurrentPersona: () => Persona;
  getPersonaForUser: (user: UserLike | null | undefined) => Persona;
  getHighlightedNavItems: () => string[];
}

export const usePersonaStore = create<PersonaState>((set, get) => ({
  activePersona: 'all',
  currentPersonaId: DEFAULT_PERSONA_ID,

  setPersona: (activePersona) => set({ activePersona }),
  togglePersona: (persona) =>
    set({
      activePersona: get().activePersona === persona ? 'all' : persona,
    }),
  setCurrentPersona: (personaId) => {
    if (personas[personaId]) {
      set({ currentPersonaId: personaId });
    }
  },
  getCurrentPersona: () => {
    const id = get().currentPersonaId;
    return personas[id] ?? personas[DEFAULT_PERSONA_ID];
  },
  getPersonaForUser: (user) => {
    const candidate = user?.personaId;
    if (candidate && personas[candidate]) {
      return personas[candidate];
    }
    return personas[get().currentPersonaId] ?? personas[DEFAULT_PERSONA_ID];
  },
  getHighlightedNavItems: () => {
    const persona =
      personas[get().currentPersonaId] ?? personas[DEFAULT_PERSONA_ID];
    return persona.highlightedNavItems;
  },
}));
